import axios from 'axios';
import { getStoredActiveBusinessId } from './utils/businessContext';
import { resolveErrorMessage } from './constants/errorMessages';

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || (isLocalhost ? 'http://localhost:8000' : 'https://haastia.fly.dev');

axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

export const ACTIVE_BUSINESS_HEADER = 'X-Haastia-Active-Business';

// Works for AxiosHeaders instances (runtime) and plain objects (test mock);
// header names are case-insensitive either way.
const hasHeader = (headers, name) => {
  if (!headers) return false;
  if (typeof headers.has === 'function') return headers.has(name);
  const wanted = name.toLowerCase();
  return Object.entries(headers).some(
    ([key, value]) => key.toLowerCase() === wanted && value !== undefined
  );
};

// Every request carries the active workspace id. Callers opt out with
// `skipActiveBusinessHeader` (the bootstrap calls that resolve which workspace
// is active), and an explicitly supplied header is never overwritten.
export const attachActiveBusinessHeader = (config) => {
  const { skipActiveBusinessHeader, ...request } = config;
  if (skipActiveBusinessHeader) return request;

  const activeBusinessId = getStoredActiveBusinessId();
  if (!activeBusinessId || hasHeader(request.headers, ACTIVE_BUSINESS_HEADER)) return request;

  if (request.headers && typeof request.headers.set === 'function') {
    request.headers.set(ACTIVE_BUSINESS_HEADER, activeBusinessId);
  } else {
    request.headers = { ...(request.headers || {}), [ACTIVE_BUSINESS_HEADER]: activeBusinessId };
  }
  return request;
};

axios.interceptors.request.use(attachActiveBusinessHeader, undefined, { synchronous: true });

const normalizeErrorPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  if (payload.error && typeof payload.error === 'object') {
    const structuredError = payload.error;
    const message = resolveErrorMessage({
      message: structuredError.message || payload.message,
      code: structuredError.code || payload.code,
    });

    return {
      ...payload,
      error: message,
      message,
      code: structuredError.code || payload.code,
      fieldErrors: structuredError.fieldErrors || payload.fieldErrors,
      errorDetails: structuredError,
    };
  }

  return payload;
};

axios.interceptors.response.use(
  (response) => {
    if (response?.data) {
      response.data = normalizeErrorPayload(response.data);
    }
    return response;
  },
  (error) => {
    if (error?.response?.data) {
      error.response.data = normalizeErrorPayload(error.response.data);
    }
    return Promise.reject(error);
  }
);

export default axios;
